import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { parseISO, format } from 'date-fns';
import { utcToZonedTime } from 'date-fns-tz';
import apiService from '../services/api';
import { APILogResponse } from '../types/api';
import { AlertCircle, CheckCircle } from 'lucide-react';
import LoadingSpinner from '../components/LoadingSpinner';

const LogsPage: React.FC = () => {
  const { data: logs, isLoading } = useQuery<APILogResponse[]>({
    queryKey: ['logs'],
    queryFn: (): Promise<APILogResponse[]> => apiService.getApiLogs(100, 0),
  });

  // Helper function to format UTC timestamp to local timezone
  const formatUtcToLocal = (utcTimestamp: string): string => {
    const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    // Ensure timestamp is treated as UTC - append 'Z' if no timezone info
    const utcString = utcTimestamp.endsWith('Z') || utcTimestamp.includes('+') || (utcTimestamp.match(/[-+]\d{2}:\d{2}$/))
      ? utcTimestamp 
      : utcTimestamp + 'Z';
    return format(utcToZonedTime(parseISO(utcString), userTimezone), 'MMM dd, HH:mm:ss');
  };

  if (isLoading) {
    return <LoadingSpinner />;
  }

  return (
    <div className="space-y-6">
      <div className="card overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead>
            <tr>
              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Time</th>
              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Service</th>
              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Endpoint</th>
              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Response Time</th>
              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Error</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {(logs ?? []).map((log: APILogResponse) => (
              <tr key={log.id}>
                <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-700">
                  {formatUtcToLocal(log.created_at)}
                </td>
                <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-700">
                  {log.service_name}
                </td>
                <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-700">
                  {log.endpoint}
                </td>
                <td className="px-4 py-2 whitespace-nowrap text-sm">
                  {log.status_code >= 200 && log.status_code < 300 ? (
                    <span className="inline-flex items-center text-green-600">
                      <CheckCircle className="h-4 w-4 mr-1" />
                      {log.status_code}
                    </span>
                  ) : (
                    <span className="inline-flex items-center text-red-600">
                      <AlertCircle className="h-4 w-4 mr-1" />
                      {log.status_code}
                    </span>
                  )}
                </td>
                <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-700">
                  {log.response_time ? `${log.response_time.toFixed(2)} ms` : 'N/A'}
                </td>
                <td className="px-4 py-2 whitespace-nowrap text-sm text-red-600">
                  {log.error_message || '-'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default LogsPage; 